// JIRA - PY-7767
// https://performyard.atlassian.net/browse/PY-7767
// FIGMA
// https://www.figma.com/design/eujlKvoVEVyxe4l1JMsivU/PY-Design-System-v1.0?node-id=10616-2963&m=dev
//dependencies
import { cva } from "class-variance-authority";
import * as React from "react";

//icons
import { IconDefinition, faXmarkCircle } from "@performyard/fontawesome/pro-solid-svg-icons";

//context
import { PageBannerProvider, usePageBannerContext } from "./PageBannerContext";

// atoms
import { ButtonIcon, ButtonStandard, ButtonStandardProps, Icon, IconProps, Text } from "../../atoms";

const IconColorsMap: Record<PageBannerProps["color"], IconProps["color"]> = {
	primary: "primary-5",
	green: "green-5",
	red: "red-5",
};

const pageBannerVariants = cva(
	"box-border flex w-full max-w-full items-start justify-start overflow-hidden rounded-1 p-2 text-neutral-9",
	{
		variants: {
			color: {
				primary: "bg-primary-1",
				green: "bg-green-1",
				red: "bg-red-1",
			},
			size: {
				large: "text-paragraph-2",
				small: "text-caption",
			},
		},
		defaultVariants: {
			color: "primary",
			size: "small",
		},
	}
);

interface PageBannerProps extends React.HTMLAttributes<HTMLDivElement> {
	/**
	 * The color theme of the PageBanner
	 */
	color: "primary" | "green" | "red";
	/**
	 * The size of the PageBanner, small or large
	 * @default "small"
	 */
	size?: "small" | "large";

	/**
	 * The title of the PageBanner
	 */
	mainTitle: string | React.ReactNode;

	/**
	 * The subtitle of the PageBanner
	 */
	subTitle?: string | React.ReactNode;

	/**
	 * The icon of the PageBanner, can be an icon from fontawesome or an Icon component
	 */
	icon?: IconDefinition | React.ReactElement<typeof Icon>;

	/**
	 * The right action button of the PageBanner, propped to ButtonStandard component with href and newTab props
	 */
	rightActionButton?: PageBannerActionButtonProps;
	/**
	 *  The left action button of the PageBanner
	 */
	bottomActionButton?: PageBannerActionButtonProps;

	/**
	 *  The function to call when the PageBanner is dismissed, button is not rendered when this is not provided
	 */
	onDismiss?: () => void;

	/**
	 * test id for the component
	 */
	"data-testid"?: string;

	/**
	 *  The class name to apply to the component
	 */
	dangerouslySetClassName?: string;
}

/**
 * Page Banner component, used to display a banner with a title, subtitle, icon, and action buttons
 */
const PageBanner = React.forwardRef<HTMLDivElement, PageBannerProps>(function PageBanner(
	props: PageBannerProps,
	ref: React.Ref<HTMLDivElement>
) {
	const {
		color,
		size = "small",
		mainTitle,
		subTitle,
		icon,
		rightActionButton,
		bottomActionButton,
		onDismiss,
		"data-testid": dataTestId = "page-banner", // default value
		dangerouslySetClassName,
	} = props;

	const iconComponent = React.isValidElement(icon) || !icon ? icon : <PageBannerIcon icon={icon} />;
	const mainTitleComponent = <PageBannerMainTitle>{mainTitle}</PageBannerMainTitle>;
	const subTitleComponent = <PageBannerSubTitle>{subTitle}</PageBannerSubTitle>;

	return (
		<PageBannerProvider size={size} color={color}>
			<div
				ref={ref}
				className={pageBannerVariants({ color, size, className: dangerouslySetClassName })}
				data-testid={dataTestId}
			>
				<div className="flex w-full shrink items-start justify-start gap-2">
					{icon && (
						<div className={`${size === "large" ? "h-8" : "h-6"} flex items-center`}>{iconComponent}</div>
					)}
					<div className={`flex h-full min-w-0 max-w-full shrink grow flex-col justify-center`}>
						<div className={`pt-1 ${size === "large" ? "h-min-8" : "h-min-6"}`}>{mainTitleComponent}</div>
						{subTitleComponent}
						{bottomActionButton && (
							<div className="mt-2">
								<PageBannerActionButton {...bottomActionButton} />
							</div>
						)}
					</div>
					{rightActionButton && (
						<div className="shrink-0">
							<PageBannerActionButton {...rightActionButton} />
						</div>
					)}
					{onDismiss && (
						<div className={`${size === "large" ? "h-8" : "h-6"} flex shrink-0 items-center`}>
							<PageBannerDismissButton onDismiss={onDismiss} />
						</div>
					)}
				</div>
			</div>
		</PageBannerProvider>
	);
});

PageBanner.displayName = "PageBanner";

const PageBannerIcon: React.FC<IconProps> = ({ icon }: { icon: IconDefinition }) => {
	const { size, color } = usePageBannerContext();
	return <Icon icon={icon} color={IconColorsMap[color]} size={size === "large" ? 24 : 16} />;
};
PageBannerIcon.displayName = "PageBannerIcon";
type PageBannerActionButtonProps = ButtonStandardProps & { href?: string; newTab?: boolean };

const PageBannerActionButton: React.FC<PageBannerActionButtonProps> = (props) => {
	const { href, newTab = false, children, ...rest } = props;
	const { size, color } = usePageBannerContext();
	let button = (
		<div className="w-fit">
			<ButtonStandard color={color} size={size === "large" ? "medium" : "small"} {...rest}>
				<Text size={size === "large" ? "paragraph-3" : "caption"} weight="regular">
					{children}
				</Text>
			</ButtonStandard>
		</div>
	);
	if (href) {
		button = (
			<a
				href={href}
				target={newTab ? "_blank" : undefined}
				rel={newTab ? "noreferrer" : undefined}
				data-testid="page-banner-action-link"
			>
				{button}
			</a>
		);
	}
	return button;
};
PageBannerActionButton.displayName = "PageBannerActionButton";

const PageBannerMainTitle = ({ children }: { children: React.ReactNode }) => {
	const { size } = usePageBannerContext();
	return (
		<Text size={size === "large" ? "paragraph-2" : "caption"} weight="semi-bold">
			{children}
		</Text>
	);
};

PageBannerMainTitle.displayName = "PageBannerMainTitle";

const PageBannerSubTitle = ({ children }: { children: React.ReactNode }) => {
	const { size } = usePageBannerContext();
	return (
		<Text size={size === "large" ? "paragraph-2" : "caption"} weight="regular">
			{children}
		</Text>
	);
};

PageBannerSubTitle.displayName = "PageBannerSubTitle";

const PageBannerDismissButton = ({ onDismiss = () => {} }: { onDismiss: PageBannerProps["onDismiss"] }) => {
	const { size, color } = usePageBannerContext();
	return (
		<ButtonIcon
			data-testid="dismiss-button"
			color={color}
			onClick={onDismiss}
			size={size === "large" ? 24 : 16}
			icon={faXmarkCircle}
		/>
	);
};

export { PageBanner, type PageBannerProps };
